import { useEffect, useRef } from "react";
import { useGestock } from "../../GestockContext";
import { useStations } from "../../useOilmed";
import { useCiternes } from "./useCiternes";
import { useStationHistory } from "./useStationHistory";
import StationCard from "./StationCard";
import StationHistoryPanel from "./StationHistoryPanel";
import { fmtDec } from "../../utils";
import { isWithinRange } from "../../monthUtils";

export default function GStations() {
  const { produit, monthRange } = useGestock();
  const { stations, stationsArray, alertes, loading } = useStations(produit);
  const prodLabel = produit === "essence" ? "Super" : "Go";

  // Custom hooks
  const citernesMap = useCiternes(produit);
  const {
    selectedStation,
    stationHistory,
    fetchStationHistory,
    clearSelectedStation,
    allStationsHistory,
    fetchAllStationsHistory,
  } = useStationHistory(produit);

  // Track if we've already loaded the history
  const hasLoadedRef = useRef(false);

  // Reset when product changes
  useEffect(() => {
    hasLoadedRef.current = false;
  }, [produit]);

  // Auto-load all stations history when page loads (no station selected)
  useEffect(() => {
    console.log("[GStations] useEffect triggered:", {
      selectedStation: selectedStation?.id || null,
      stationsCount: stationsArray.length,
      loading,
      hasLoaded: hasLoadedRef.current,
    });

    if (
      !selectedStation &&
      stationsArray.length > 0 &&
      !loading &&
      !hasLoadedRef.current
    ) {
      console.log("[GStations] Loading all stations history...");
      hasLoadedRef.current = true;
      fetchAllStationsHistory(stationsArray);
    }
  }, [selectedStation, stationsArray, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate real-time alerts based on citernes data
  const realTimeAlertes = stationsArray
    .map((sta) => {
      const stationCiternes = citernesMap[sta.id] || {};
      const stk = Object.values(stationCiternes).reduce(
        (sum, c) => sum + (c.volume || 0),
        0,
      );
      const seuil = sta.seuil?.[produit] ?? 0;

      return {
        stationId: sta.id,
        label: sta.label,
        stock: stk,
        seuil: seuil,
        vide: stk === 0,
        isLow: stk <= seuil && stk > 0,
      };
    })
    .filter((a) => a.stock <= a.seuil);

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-title">
          Stock <span>Stations</span>
        </div>
        <div className="page-sub">
          {"// NIVEAUX TEMPS RÉEL · "}
          {prodLabel.toUpperCase()}
        </div>
      </div>

      {/* Alerts */}
      {realTimeAlertes.map((a) => (
        <div key={a.stationId} className="alert alert-red">
          🔴 <strong>{a.label}</strong> —{" "}
          {a.vide ? "Vide" : `Critique (${fmtDec(a.stock, 3)} m³)`}. Seuil:{" "}
          {a.seuil} m³.
        </div>
      ))}

      {/* Station Cards Grid */}
      <div className="sta-grid">
        {loading ? (
          <div className="empty">Chargement…</div>
        ) : (
          stationsArray.map((sta) => (
            <StationCard
              key={sta.id}
              station={sta}
              citernesMap={citernesMap}
              selectedStation={selectedStation}
              onClick={() => fetchStationHistory(sta)}
              produit={produit}
            />
          ))
        )}
      </div>

      {/* Station History Panel */}
      <StationHistoryPanel
        selectedStation={selectedStation}
        stationHistory={{
          ...(selectedStation ? stationHistory : allStationsHistory),
          movements: (selectedStation ? stationHistory : allStationsHistory).movements.filter(
            (mov) => isWithinRange(mov.timestamp || mov.date, monthRange),
          ),
        }}
        onClose={clearSelectedStation}
        produit={produit}
      />
    </div>
  );
}
